import {
  ChangeDetectionStrategy,
  Component,
  input,
  output
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import {
  AsyncDataItem,
  IExpenseSummary,
  isError,
  isLoading
} from '@pharvio/core/shared';
import { MoneyPipe } from '@pharvio/core/ui';
import { ExpensesPaths } from '../../../../core/constants/routes/expenses-routes';

/**
 * "Expenses this month" for the dashboard.
 *
 * Presentational: it renders whatever summary it is handed and asks to be
 * reloaded via `retry`. The dashboard owns the fetch, which is what lets the
 * page's own refresh button and branch selector drive this card too — when it
 * dispatched for itself, neither reached it.
 */
@Component({
  selector: 'app-expenses-month-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatButtonModule, MatIconModule, MoneyPipe],
  templateUrl: './expenses-month-card.component.html'
})
export class ExpensesMonthCardComponent {
  readonly summary = input.required<AsyncDataItem<IExpenseSummary | null>>();

  /**
   * Query params for "See all expenses", so the list opens on the same month
   * and branch these figures were computed for.
   */
  readonly linkQueryParams = input.required<Record<string, string>>();

  /** Asks the dashboard to re-fetch — the "Try again" button. */
  readonly retry = output<void>();

  protected readonly isLoading = isLoading;
  protected readonly isError = isError;

  readonly expensesPath = ExpensesPaths.Base;

  /** Bar width as a share of the largest category, never of the total. */
  barWidth(summary: IExpenseSummary, amountMinor: number): string {
    const top = Math.max(...summary.topCategories.map((c) => c.amountMinor), 1);
    return `${Math.round((amountMinor / top) * 100)}%`;
  }
}
